import { Card, Text, Group, createStyles, Button, rem, Badge } from '@mantine/core';
import { Icon24Hours, IconApi, IconCheck } from '@tabler/icons';
import { useStore } from "../store";
import { notifications } from '@mantine/notifications';
import { observer } from 'mobx-react-lite';
import BigNumber from "bignumber.js";
import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { VALIDATE_PAYMENT } from '../graphql/mutations';
import { ME, GET_PROJECTS } from '../graphql/queries';
import marketAbi from '../abis/Market.json';
import usdtAbi from '../abis/USDT.json';


const useStyles = createStyles((theme) => ({
  card: {
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
  },

  imageSection: {
    padding: theme.spacing.md,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderBottom: `${rem(1)} solid ${
      theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]
    }`,
  },

  label: {
    marginBottom: theme.spacing.xs,
    lineHeight: 1,
    fontWeight: 700,
    fontSize: theme.fontSizes.xs,
    letterSpacing: rem(-0.25),
    textTransform: 'uppercase',
  },

  section: {
    padding: theme.spacing.md,
    borderTop: `${rem(1)} solid ${
      theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]
    }`,
  },

  icon: {
    marginRight: rem(5),
    color: theme.colorScheme === 'dark' ? theme.colors.dark[2] : theme.colors.gray[5],
  },
}));

const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

const TX_CONFIRM_POLL_INTERVAL_MS = 2000;
const TX_CONFIRM_MAX_ATTEMPTS = 15;

// TronWeb decodes a single-output call differently depending on whether the ABI
// output is named (wrapped, e.g. `{ remaining: BigNumber }`) or unnamed (unwrapped
// to the raw BigNumber). Normalizing here avoids silently producing NaN, which
// previously made allowance/balance comparisons always pass and skipped approve().
const toBigNumber = (value: any): BigNumber => {
  if (value === null || value === undefined) {
    return new BigNumber(NaN);
  }
  if (typeof value === 'object') {
    if ('remaining' in value) return toBigNumber(value.remaining);
    if ('_hex' in value) return new BigNumber(value._hex);
    if (0 in value) return toBigNumber(value[0]);
  }
  return new BigNumber(value.toString());
};

// Sent transactions only return a txid; they are not guaranteed to have landed
// or succeeded yet. Poll for the receipt instead of assuming a fixed delay is enough.
const waitForTransactionSuccess = async (txId: string): Promise<boolean> => {
  for (let attempt = 0; attempt < TX_CONFIRM_MAX_ATTEMPTS; attempt++) {
    await delay(TX_CONFIRM_POLL_INTERVAL_MS);
    const info = await window.tronWeb.trx.getTransactionInfo(txId);
    if (info?.receipt?.result === 'SUCCESS') {
      return true;
    }
    if (info?.receipt?.result) {
      return false;
    }
  }
  return false;
};

interface PlanBlockProps {
  plan: any;
  showBuyButton: boolean;
}


export const PlanBlock = observer((props: PlanBlockProps) => {
  const [validatePayment] = useMutation(VALIDATE_PAYMENT, {
    refetchQueries: [{ query: ME }, {query: GET_PROJECTS}],
    onCompleted(data) {
      console.log(data);
      if (data.validatePayment) {
        notifications.show({
          title: 'Success',
          color: 'green',
          message: 'Congratulations. You payment was accepted!',
        });
      } else {
        notifications.show({
          title: 'Error',
          color: 'error',
          message: 'Some error occurred!',
        });
      }
    }
  });

  const [isLoading, setIsLoading] = useState(false);
  const { classes } = useStyles();
  let { user, wallet } = useStore();
  const FEATURE_LABELS: Record<string, string> = {
    websocket: "Websocket Connection",
    custom_domain: "Custom Domains",
    priority_support: "24/7 Priority Support",
    rest_api: "REST API Access",
  };

  const formatFeatureLabel = (featureId: string) => {
    if (FEATURE_LABELS[featureId]) {
      return FEATURE_LABELS[featureId];
    }
    return featureId
      .replace(/[_-]/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  let featureData: any[] = [];
  featureData.push({
    label: `${props.plan.rateLimit} per ${props.plan.ratePeriod} second(s)`,
    icon: IconApi,
  })
  featureData.push({
    label: `${props.plan.requestsPerMonth.toLocaleString()} reqs/month`,
    icon: IconApi,
  })
  featureData.push({
    label: `REST API`,
    icon: IconApi,
  });

  if (props.plan.features && Array.isArray(props.plan.features)) {
    props.plan.features.forEach((feat: string) => {
      featureData.push({
        label: formatFeatureLabel(feat),
        icon: IconCheck,
      });
    });
  }

  const connectWallet = async () => {
    if (window.tronWeb) {
      const res = await window.tronLink.request({method: 'tron_requestAccounts'});
      if (res.code === 200) { // User acceptance of authorization
        wallet.setAddress(window.tronWeb.defaultAddress.base58);
        return ["", ""];
      } else if (res.code === 4000) { // In the queue, no need to duplicate commits
        return ["", ""];
      } else if (res.code === 4001) {// User refusal to authorize
        return ["red", "REJECTED"]
      } else {
        return ["red", "Please login to TronLink extention wallet first."];
      }
    } else {
      //wallet is not detected at all
      return ["red", "WALLET NOT DETECTED"];
    }
  };

  const buy = async() => {
    setIsLoading(true);
    try {
      if (!user.props?.id) {
        notifications.show({
          title: 'Error',
          color: 'red',
          message: 'User is not connected',
        });
        return;
      }

      let [color, error] = await connectWallet();
      if (error) {
        notifications.show({
          title: 'Error',
          color: color,
          message: error,
        });
        return;
      }

      const MARKET_ADDRESS = "TDH9dX6HxXcqBY5VRNF644b5Vdxh2EfvBm";
      const USDT_ADDRESS = "TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t";

      let usdtContract = await window.tronWeb.contract(usdtAbi, USDT_ADDRESS);
      let marketContract = await window.tronWeb.contract(marketAbi, MARKET_ADDRESS);

      // The amount actually charged is whatever the contract has stored for this
      // plan, which can drift from the price shown in the app (it is only updated
      // via a separate setPlan() call). Source the payment amount from the chain
      // so approve()/pay() always agree with what pay() will actually require.
      const onChainPlan = await marketContract.getPlan(props.plan.slug).call();
      const pricePlan = toBigNumber(onChainPlan.amount);
      if (pricePlan.isNaN() || pricePlan.isZero()) {
        notifications.show({
          title: 'Error',
          color: 'red',
          message: 'This plan is not available for purchase right now',
        });
        return;
      }

      const usdtBalance = toBigNumber(await usdtContract.balanceOf(wallet.address).call());
      if (usdtBalance.comparedTo(pricePlan) < 0) {
        notifications.show({
          title: 'Error',
          color: 'red',
          message: 'You do not have enough tokens',
        });
        return;
      }

      const currentAllowance = toBigNumber(await usdtContract.allowance(wallet.address, MARKET_ADDRESS).call());
      if (currentAllowance.comparedTo(pricePlan) < 0) {
        const approveTransId = await usdtContract.approve(MARKET_ADDRESS, pricePlan.toString()).send();
        const approved = await waitForTransactionSuccess(approveTransId);
        if (!approved) {
          notifications.show({
            title: 'Error',
            color: 'red',
            message: 'The USDT approval transaction failed. Please try again.',
          });
          return;
        }
      }

      // Re-check right before paying: a stale or misread allowance must never
      // let pay() fire, since that only wastes the user's TRX on a doomed call.
      const confirmedAllowance = toBigNumber(await usdtContract.allowance(wallet.address, MARKET_ADDRESS).call());
      if (confirmedAllowance.comparedTo(pricePlan) < 0) {
        notifications.show({
          title: 'Error',
          color: 'red',
          message: 'USDT allowance is still insufficient. Please try again.',
        });
        return;
      }

      const payTransId = await marketContract.pay(props.plan.slug, user.props?.id).send();
      const paid = await waitForTransactionSuccess(payTransId);
      if (!paid) {
        notifications.show({
          title: 'Error',
          color: 'red',
          message: 'The payment transaction failed on-chain. Please try again or contact support.',
        });
        return;
      }

      validatePayment({
        variables: {
          txHash: payTransId
        }
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Something went wrong while processing your payment';
      notifications.show({
        title: 'Error',
        color: 'red',
        message,
      });
    } finally {
      setIsLoading(false);
    }
  }

  if (props.plan.slug === "vip") {
    featureData.push({
      label: `24h/7days support`,
      icon: Icon24Hours,
    });
  }
  const features = featureData.map((feature) => (
    <div key={feature.label}>
      <Group>
        <feature.icon size="1.05rem" className={classes.icon} stroke={1.5} />
        <Text size="xs">{feature.label}</Text>
      </Group>
    </div>
  ));

  return (
    <Card withBorder radius="md" className={classes.card}>
      <Group position="apart" mt="md">
        <div>
          <h2>{props.plan.title}</h2>
          <Text fz="xs" c="dimmed">
            {props.plan.description}
          </Text>
        </div>
      </Group>

      <Card.Section className={classes.section} mt="md">
        <Text fz="sm" c="dimmed" className={classes.label}>
          Basic configuration
        </Text>

        {features}
      </Card.Section>

      <Card.Section className={classes.section}>
        <Group
          spacing={30}
          sx={(theme) => ({
            [theme.fn.smallerThan('xs')]: {
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: theme.spacing.md,
            },
          })}
        >
          <div>
            <Text fz="xl" fw={700} sx={{ lineHeight: 1 }}>
              {props.plan.price ? (
                <>{parseInt(props.plan.price)} {props.plan.currency}</>
              ) : (
                <Badge color="green">Free</Badge>
              )}
            </Text>
            <Text fz="sm" c="dimmed" fw={500} sx={{ lineHeight: 1 }} mt={3}>
              {props.plan.price && "per month"}
            </Text>
          </div>
          {user.props?.data?.planSlug === props.plan.slug ? (
            <Button
              color="green"
              radius="xl"
              style={{ flex: 1 }}
              sx={(theme) => ({
                [theme.fn.smallerThan('xs')]: {
                  width: '100%',
                },
              })}
            >
              Your current plan
            </Button>
          ) : (
            <>
              {props.plan.price && props.showBuyButton && (
                <Button
                  loading={isLoading}
                  onClick={buy}
                  color="blue"
                  radius="xl"
                  style={{ flex: 1 }}
                  sx={(theme) => ({
                    [theme.fn.smallerThan('xs')]: {
                      width: '100%',
                    },
                  })}
                >
                  {wallet.address ? "Buy" : "Connect wallet -> Buy"}
                </Button>
              )}
            </>
          )}
        </Group>
      </Card.Section>
    </Card>
  );
})
